import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Programs', path: '/services' },
  { name: 'About', path: '/about' },
  { name: 'FAQ', path: '/faq' },
  { name: 'Contact', path: '/contact' },
];

export default function NavV2() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0d1b2a]/90 backdrop-blur-xl border-b border-white/10'
          : 'bg-white/80 backdrop-blur-md border-b border-slate-200'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2 group">
            <motion.img
              whileHover={{ scale: 1.1 }}
              src="/logo-btt.svg"
              alt="Better Together Training"
              className="h-7 w-7 sm:h-9 sm:w-9"
            />
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex flex-col leading-tight"
            >
              <span className="text-lg sm:text-2xl font-bold text-[#3B9AE1]">
                Better Together Training
              </span>
              <span className={`text-[10px] sm:text-xs tracking-widest uppercase ${
                scrolled ? 'text-[#94a3b8]' : 'text-slate-500'
              }`}>
                Family Sports &amp; Fitness
              </span>
            </motion.div>
          </Link>

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const active = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative font-medium transition-colors hover:text-[#3B9AE1] ${
                    active ? 'text-[#3B9AE1]' : scrolled ? 'text-white' : 'text-slate-700'
                  }`}
                >
                  {link.name}
                  {active && (
                    <motion.div
                      layoutId="v2-nav-indicator"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#3B9AE1]"
                      initial={false}
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
            <Link
              to="/services"
              className="px-5 py-2 bg-[#2563eb] text-white font-semibold rounded-full transition hover:brightness-110 text-sm"
            >
              Book Now
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden p-2 rounded-lg border ${scrolled ? 'border-white/10 hover:bg-white/10' : 'border-slate-200 hover:bg-slate-100'}`}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className={`w-6 h-6 ${scrolled ? 'text-[#e2e8f0]' : 'text-slate-700'}`} /> : <Menu className={`w-6 h-6 ${scrolled ? 'text-[#e2e8f0]' : 'text-slate-700'}`} />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden rounded-xl border border-white/10 bg-[#12263a] mt-2"
            >
              <div className="px-4 py-6 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block py-2.5 px-4 rounded-lg transition-colors ${
                      location.pathname === link.path
                        ? 'bg-[#2563eb]/15 text-[#3B9AE1]'
                        : 'text-white hover:bg-white/5'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link
                  to="/services"
                  className="block mt-2 py-3 px-4 text-center bg-[#2563eb] text-white font-semibold rounded-lg"
                >
                  Book Now
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}
